/**
 * @file        src/pages/ForgotPassword.jsx
 * @description Admin password reset, in the three steps the API enforces:
 *              email → 6-digit code (emailed) → new password.
 *
 *              The code is never trusted by this page: it is verified server-side,
 *              which hands back a short-lived reset token, and THAT is what sets the
 *              password. So a user who skips to step 3 in devtools has nothing to
 *              submit with.
 *
 *              Step 1 always claims success, even for an email that has no admin
 *              account — the server deliberately refuses to say which addresses are
 *              real, and the UI must not undo that by rendering "no such user".
 * @author      Kinetika Labs
 * @created     2026-07-14
 * @changelog
 *   - 2026-07-14 Claude — Initial.
 *   - 2026-07-31 Claude — Tenant logo from Site Settings (bundled PNG is now the
 *                          fallback), matching Login.jsx. Reset flow untouched.
 *   - 2026-09-17 Claude — OTP placeholder is dots, not "123456", which read as a
 *                          pre-filled code.
 *   - 2026-09-17 Claude — Both new-password fields are now <PasswordInput>.
 *                          "Passwords do not match" is the only feedback a
 *                          typo got, and it names neither field.
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { post } from '../api/client';
import { useBranding } from '../lib/useBranding';
import { toast } from '../lib/toast';
import Aurora from '../components/Aurora';
import logoFull from '../assets/logo-full.png';
import PasswordInput from '../components/PasswordInput';

const errMsg = (e, fallback) => e?.response?.data?.error?.message || fallback;

// Must match the server's rule (auth.routes resetPasswordSchema). The server is the
// one that enforces it; this only keeps the user from submitting a doomed form.
const MIN_PWD = 10;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { logoUrl, name } = useBranding();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [devNotice, setDevNotice] = useState('');

  async function sendCode(e) {
    e?.preventDefault();
    setError('');
    // Our message, our card — not the browser's grey "Please enter an email address"
    // balloon. The server validates the address again regardless.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter a valid email address.');
      toast.err('Invalid email', 'Enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      const res = await post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setNotice(res.data?.message || 'If that email belongs to an admin account, a code is on its way.');
      // Development, no SMTP: the server names the code that development accepts. It
      // is NOT pre-filled — typing it in is what tells you whether this address is
      // actually an admin account, and a pre-filled box made a refused code look like
      // an issued one.
      setDevNotice(res.data?.devNotice || '');
      setOtp('');
      setStep(2);
    } catch (err) {
      // The server names the failure now (NO_ADMIN_ACCOUNT / rate limited), so surface
      // it rather than a house message — "no admin account with this email" is the
      // whole point of the request the user just made.
      const msg = errMsg(err, 'Could not send the code. Try again.');
      setError(msg);
      toast.err('Cannot send code', msg);
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await post('/auth/verify-reset-otp', {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
      });
      setResetToken(res.data.resetToken);
      setNotice('');
      setStep(3);
    } catch (err) {
      setError(errMsg(err, 'That code is not correct.'));
    } finally {
      setBusy(false);
    }
  }

  // Shown inline, and only once the user has typed something — a field that is red
  // before it has been touched is nagging, not helping.
  const tooShort = pwd.length > 0 && pwd.length < MIN_PWD;
  const mismatch = pwd2.length > 0 && pwd !== pwd2;
  const canSubmit = pwd.length >= MIN_PWD && pwd === pwd2;

  async function savePassword(e) {
    e.preventDefault();
    setError('');
    if (!canSubmit) return;
    setBusy(true);
    try {
      await post('/auth/reset-password', {
        resetToken,
        newPassword: pwd,
        confirmPassword: pwd2,
      });
      toast.ok('Password changed', 'Sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      setError(errMsg(err, 'Could not set the password. Start again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <Aurora />
      <article className="glass login-card">
        <div className="brand-row">
          {/* Tenant logo when configured; bundled default otherwise, and also
              if the configured URL fails to load (image deleted / moved). */}
          <img
            src={logoUrl || logoFull}
            alt={name || 'Neon Holidays'}
            onError={(e) => { e.currentTarget.src = logoFull; }}
          />
          <span className="brand-sub" style={{ borderBottom: 'none', padding: 0 }}>
            Travel-Tech Admin Console
          </span>
        </div>

        {/* Steps 1 and 2 carry no title: the brand block plus the instruction line
            already say what the screen is for, and "Reset password" over "Enter your
            admin email" was saying it twice. The final step keeps its heading because
            that one names the action the user is about to take. */}
        {step === 3 ? <h1>Set a new password</h1> : null}

        {error ? <div className="err-banner">{error}</div> : null}

        {step === 1 && (
          <form onSubmit={sendCode} noValidate>
            <p className="brand-sub" style={{ border: 'none', padding: '0 0 12px', textAlign: 'left' }}>
              Enter your admin email. We&rsquo;ll send a 6-digit code to it.
            </p>
            <div className="field">
              <label htmlFor="fp-email">Email</label>
              <input
                id="fp-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@neonholidays.com"
              />
            </div>
            <button className="btn primary" type="submit" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyCode} noValidate>
            {notice ? (
              <p className="brand-sub" style={{ border: 'none', padding: '0 0 12px', textAlign: 'left' }}>
                {notice}
              </p>
            ) : null}
            {/* Only ever rendered when the server says it could not send mail AND it is
                not running in production. It is a scaffold, and it should look like one. */}
            {devNotice ? (
              <div
                className="err-banner"
                style={{ background: 'rgba(245,158,11,.12)', color: '#b45309', borderColor: 'rgba(245,158,11,.35)' }}
              >
                {devNotice}
              </div>
            ) : null}
            <div className="field">
              <label htmlFor="fp-otp">6-digit code</label>
              {/* A literal "123456" placeholder read as a pre-filled value rather than a
                  hint. Dots show the shape — six characters — without looking like a code. */}
              <input
                id="fp-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••••"
                style={{ letterSpacing: '6px', fontWeight: 700 }}
              />
              {/* We only reach step 2 for a confirmed admin account (the server refuses
                  anything else outright), so this can state the send plainly. */}
              <span className="field-hint" style={{ fontSize: '.72rem', color: 'var(--muted)' }}>
                Sent to {email}. It expires shortly and can be used once.
              </span>
            </div>
            <button className="btn primary" type="submit" disabled={busy || otp.length !== 6}>
              {busy ? 'Checking…' : 'Verify code'}
            </button>
            <button
              type="button"
              className="btn"
              style={{ marginTop: 8 }}
              disabled={busy}
              onClick={sendCode}
            >
              Resend code
            </button>
          </form>
        )}

        {step === 3 && (
          // noValidate: the browser's own bubble ("Please lengthen this text to 10
          // characters…") is an OS-styled popup we cannot theme, and it appears on a
          // grey system tooltip in the middle of our card. The same rules are stated
          // inline instead, in the console's own type — and re-checked on the server,
          // which is what actually enforces them.
          <form onSubmit={savePassword} noValidate>
            <div className="field">
              <label htmlFor="fp-pwd">New password</label>
              <PasswordInput
                id="fp-pwd"
                autoComplete="new-password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="At least 10 characters"
                style={tooShort ? { borderColor: 'var(--rose)' } : undefined}
              />
              {tooShort ? (
                <span className="field-error">
                  Use at least 10 characters — {10 - pwd.length} more to go.
                </span>
              ) : (
                <span className="field-hint" style={{ fontSize: '.72rem', color: 'var(--muted)' }}>
                  At least 10 characters.
                </span>
              )}
            </div>
            <div className="field">
              <label htmlFor="fp-pwd2">Confirm new password</label>
              <PasswordInput
                id="fp-pwd2"
                autoComplete="new-password"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                placeholder="Repeat it"
                style={mismatch ? { borderColor: 'var(--rose)' } : undefined}
              />
              {mismatch ? <span className="field-error">Passwords do not match.</span> : null}
            </div>
            <button className="btn primary" type="submit" disabled={busy || !canSubmit}>
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </form>
        )}

        <p style={{ marginTop: 14, textAlign: 'center', fontSize: '.8rem' }}>
          <Link to="/login">Back to sign in</Link>
        </p>
      </article>
    </div>
  );
}
